// In-memory ring buffer of recent user/system events ("breadcrumbs") that get
// attached to error reports so we can see what led up to a crash.

export type Breadcrumb = {
  timestamp: string;
  category: "navigation" | "click" | "fetch" | "console" | "auth" | "custom";
  message: string;
  data?: Record<string, unknown>;
};

const MAX_BREADCRUMBS = 30;
const buffer: Breadcrumb[] = [];

export function addBreadcrumb(crumb: Omit<Breadcrumb, "timestamp">) {
  buffer.push({ timestamp: new Date().toISOString(), ...crumb });
  if (buffer.length > MAX_BREADCRUMBS) buffer.shift();
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer];
}

export function clearBreadcrumbs() {
  buffer.length = 0;
}

let installed = false;

export function installBreadcrumbs() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Navigation
  let lastPath = window.location.pathname + window.location.search;
  addBreadcrumb({ category: "navigation", message: `load ${lastPath}` });
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  const recordNav = (type: string) => {
    const next = window.location.pathname + window.location.search;
    if (next !== lastPath) {
      addBreadcrumb({ category: "navigation", message: `${type} ${lastPath} → ${next}` });
      lastPath = next;
    }
  };
  history.pushState = function (...args) {
    const r = origPush.apply(this, args);
    recordNav("push");
    return r;
  };
  history.replaceState = function (...args) {
    const r = origReplace.apply(this, args);
    recordNav("replace");
    return r;
  };
  window.addEventListener("popstate", () => recordNav("pop"));

  // Clicks
  window.addEventListener(
    "click",
    (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const el = t.closest("a,button,[role=button]") as HTMLElement | null;
      if (!el) return;
      const label =
        el.getAttribute("aria-label") || el.textContent?.trim().slice(0, 80) || el.tagName.toLowerCase();
      addBreadcrumb({
        category: "click",
        message: label || el.tagName.toLowerCase(),
        data: { tag: el.tagName.toLowerCase(), id: el.id || undefined },
      });
    },
    { capture: true },
  );

  // Fetch
  const origFetch = window.fetch;
  window.fetch = async function (...args: Parameters<typeof fetch>) {
    const [input, init] = args;
    const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input);
    const method = init?.method || (input instanceof Request ? input.method : "GET");
    const start = performance.now();
    try {
      const res = await origFetch.apply(this, args);
      addBreadcrumb({
        category: "fetch",
        message: `${method} ${url} → ${res.status}`,
        data: { status: res.status, ms: Math.round(performance.now() - start) },
      });
      return res;
    } catch (err) {
      addBreadcrumb({
        category: "fetch",
        message: `${method} ${url} → failed`,
        data: { error: String(err) },
      });
      throw err;
    }
  };

  // Console errors/warnings
  const origError = console.error;
  const origWarn = console.warn;
  console.error = (...args: unknown[]) => {
    addBreadcrumb({ category: "console", message: `error: ${args.map(String).join(" ").slice(0, 200)}` });
    origError.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    addBreadcrumb({ category: "console", message: `warn: ${args.map(String).join(" ").slice(0, 200)}` });
    origWarn.apply(console, args);
  };
}
